"use client";

import { LeadForm } from "@2u-landing-pages/components";
import {
  TypeContent_liteWithoutLinkResolutionResponse,
  TypeFormWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import cn from "classnames";
import { renderEmbeddedEntry, renderMark, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import EventClient from "@2u-landing-pages/utils/event-client";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Body, Button, ButtonGroup, Container, Drawer, Heading, IconFeatherX, logEvent } from "@red-digital/bricks";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import styles from "./InlineDrawerForm.module.scss";

interface InlineDrawerFormProps {
  subtitle?: string;
  footnote?: TypeFormWithoutUnresolvableLinksResponse["fields"]["footnote"];
  showLargeLogo?: boolean;
}

interface CustomCSSProperties extends React.CSSProperties {
  "--button-background-color"?: string;
  "--button-hover-state"?: string;
  "--button-text-color"?: string;
  "--progress-bar-color"?: string;
  "--question-color"?: string;
  "--section-background-color"?: string;
}

interface EduListingPayload {
  form_data_package_id: string;
  image_url: string;
  name: string;
  title: string;
  subtitle: string;
  body?: string;
  formFootnote?: TypeFormWithoutUnresolvableLinksResponse["fields"]["footnote"];
  colors: {
    button_background_color?: string;
    button_hover_state?: string;
    button_text_color?: string;
    progress_bar_color?: string;
    question_color?: string;
    section_background_color?: string;
  };
}

interface DrawerFormData {
  title: TypeContent_liteWithoutLinkResolutionResponse["fields"]["heading"];
  subtitle: TypeContent_liteWithoutLinkResolutionResponse["fields"]["subhead"];
  body: TypeContent_liteWithoutLinkResolutionResponse["fields"]["body"];
}

export const InlineDrawerForm: React.FC<InlineDrawerFormProps> = ({ subtitle, footnote, showLargeLogo = false }) => {
  const [content, setContent] = useState<DrawerFormData | undefined>();
  const [data, setData] = useState<EduListingPayload | undefined>();
  const handleClose = (position: string) => {
    EventClient.elementClicked({
      webElement: {
        elementType: "Multi Program",
        htmlId: window.location.href,
        location: `Drawer Form - ${data?.form_data_package_id}`,
        position,
        text: "Close",
      },
    });
    logEvent("close_inline_drawer_form", data || {});

    setData(undefined);
  };

  function safeJsonParse(str: string | undefined) {
    if (!str) return str;
    try {
      return JSON.parse(str);
    } catch {
      return str;
    }
  }

  const style = React.useMemo(() => {
    const newStyle: CustomCSSProperties = {};
    newStyle["--button-background-color"] = data?.colors?.button_background_color
      ? `var(${data.colors.button_background_color})`
      : "var(--primary-6)";
    if (data?.colors?.button_hover_state) newStyle["--button-hover-state"] = `var(${data.colors.button_hover_state})`;
    if (data?.colors?.button_text_color) newStyle["--button-text-color"] = `var(${data.colors.button_text_color})`;
    if (data?.colors?.progress_bar_color) newStyle["--progress-bar-color"] = `var(${data.colors.progress_bar_color})`;
    if (data?.colors?.question_color) newStyle["--question-color"] = `var(${data.colors.question_color})`;
    if (data?.colors?.section_background_color)
      newStyle["--section-background-color"] = `var(${data.colors.section_background_color})`;
    return newStyle;
  }, [data?.colors]);

  useEffect(() => {
    const handleEduListing = ((event: CustomEvent<EduListingPayload>) => {
      logEvent("open_inline_drawer_form", event.detail);
      setContent({
        title: safeJsonParse(event.detail.title),
        subtitle: safeJsonParse(event.detail.subtitle),
        body: safeJsonParse(event.detail.body),
      });
      setData(event.detail);
    }) as EventListener;

    window.addEventListener("eduListing", handleEduListing);
    return () => {
      window.removeEventListener("eduListing", handleEduListing);
    };
  }, []);

  return (
    <Drawer
      className={styles.drawer}
      isOpen={Boolean(data?.form_data_package_id)}
      position="bottom"
      onClose={() => handleClose("background")}
      style={style}
    >
      <ButtonGroup
        className={styles.buttonGroup}
        placement="center"
        breakpoints={{ md: { placement: "right", layout: "inline" } }}
      >
        <Button
          v2
          variant="plain"
          color="light"
          icon={{ component: <IconFeatherX size="lg" /> }}
          onClick={() => handleClose("button")}
        >
          Close
        </Button>
      </ButtonGroup>
      <Container className={styles.container} md={{ flex: true, justifyContent: "space-between" }}>
        <div className={styles.content}>
          {data?.image_url && (
            <Image
              src={data?.image_url}
              alt={data?.title || ""}
              width={200}
              height={200}
              className={cn({
                [styles.smallHeaderLogo]: !showLargeLogo,
                [styles.largeHeaderLogo]: showLargeLogo,
              })}
            />
          )}
          <Heading variant="heading-2xl" type="h3" style={{ lineHeight: "1.25" }}>
            {typeof safeJsonParse(data?.title) === "string" && data?.title}
            {content?.title &&
              typeof content.title === "object" &&
              documentToReactComponents(content.title, {
                renderMark,
                renderText: (text) => renderText(text),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => children,
                },
              })}
          </Heading>
          <Body variant="body-lg">
            {typeof safeJsonParse(data?.subtitle) === "string" && data?.subtitle}
            {content?.subtitle &&
              typeof content.subtitle === "object" &&
              documentToReactComponents(content.subtitle, {
                renderMark,
                renderText: (text) => renderText(text),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => <>{children}</>,
                },
              })}
          </Body>
          <Body variant="body-md">
            {typeof safeJsonParse(data?.body) === "string" && data?.body}
            {content?.body &&
              typeof content.body === "object" &&
              documentToReactComponents(content.body, {
                renderMark,
                renderText: (text) => renderText(text),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => <>{children}</>,
                  [INLINES.EMBEDDED_ENTRY]: (node) => renderEmbeddedEntry(node, "Tile Body", content.body),
                },
              })}
          </Body>
        </div>
        <div className={styles.form}>
          {data?.form_data_package_id && (
            <LeadForm packageId={data.form_data_package_id} footnote={data.formFootnote || footnote} />
          )}
        </div>
      </Container>
    </Drawer>
  );
};
